import { Calendar, momentLocalizer } from "react-big-calendar";
import LoadingSkeleton from "../../../common/loading-skeleton/loading-skeleton";
import { Tag } from "antd";
import {removeUnderScoreWithLowerCase, replaceSpaceWithHyphen } from "../../../../utility/utility";
import { History } from "lucide-react";
import React, { useEffect, useState } from "react";
import Toolbar from "./calendar-toolbar";
import dayjs, { Dayjs } from 'dayjs';
import moment from "moment";
import { fetchMyAttendance, fetchPunches } from "../actions";
import Locale from "../../../../core/locale/locale";
import { IEmployeeAttendanceCalendarState } from "../../attendance.types";
import { mapCalendarEvent, mapEmpAttendanceData } from "../../utils";
import AttendanceCalendarEvent from "./attendance-calendar-event";
import AttendanceCalendarDateCell from "./attendance-calendar-date-cell";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);


const initialState = {
    activeRange: {
        startDate: dayjs().startOf('month').startOf("week").format(Locale.dateFormat),
        endDate: dayjs().endOf('month').endOf("week").format(Locale.dateFormat)
    },
    attendanceData: [],
    calendarEventsData: [],
    isLoading: false
};


const EmployeeAttendanceCalendar = ({employeeId}: { employeeId: string }) => {

    const [state, setState] = useState<IEmployeeAttendanceCalendarState>(initialState);
    const [selectedDateCell, setSelectedDateCell] = useState<Dayjs | null>(dayjs());
    const [currentDate, setCurrentDate] = useState(dayjs());

    useEffect(() => {
        loadData(state.activeRange.startDate, state.activeRange.endDate);
    }, []);



    async function loadData(startDate: string, endDate: string){
        let attendanceRes;
         attendanceRes = await fetchMyAttendance(employeeId, 0, 42, startDate, endDate);
        const punchRes = await fetchPunches(employeeId, 0, 42, startDate, endDate);

        //since today attendance is not fetched by attendance api.
        const isTodayInRange = dayjs().isBetween(
            dayjs(startDate),
            dayjs(endDate),
            "day",
            "[]"
        );
        if(isTodayInRange){
            let todayAttendanceData;
            const todayPunch=punchRes.find((item:any)=>item.date===dayjs().format(Locale.displayDate));
            if(todayPunch&&todayPunch.length>0){
                todayAttendanceData={
                    id: '',
                    employeeId:employeeId,
                    date: dayjs().format(Locale.dateFormat),
                    status:'PRESENT',
                    checkInTime:todayPunch[todayPunch?.length-1],
                };
            }else{
                todayAttendanceData={
                    id: '',
                    employeeId:employeeId,
                    date: dayjs().format(Locale.dateFormat),
                    status:'ABSENT',
                };
            }
            attendanceRes=[...attendanceRes,todayAttendanceData]

        }


        const calendarData = mapCalendarEvent(attendanceRes);
        //formatted Data
        const formattedRes = mapEmpAttendanceData(attendanceRes, punchRes);

        setState({
            activeRange: {
                startDate: startDate,
                endDate: endDate
            },
            attendanceData: formattedRes,
            calendarEventsData: calendarData,
            isLoading: false
        });
    }



    function onDateChange(startDate: string, endDate: string) {
        loadData(startDate, endDate);
        setState((prev) => ({
            ...prev,
            activeRange: {
                startDate: startDate,
                endDate: endDate
            }
        }));

    }


    const localizer = momentLocalizer(moment)


    const selectedRecord = state.attendanceData.find(
        item => item.date === selectedDateCell?.format(Locale.dateFormat)
    );

    return (

        <div className={'employee-attendance__calendar-section'}>
            <LoadingSkeleton loading={state.isLoading}>
                <Calendar
                    defaultView="month"
                    views={["month"]}
                    drilldownView={null}
                    date={currentDate.toDate()}
                    onNavigate={(date) => setCurrentDate(dayjs(date))}
                    className={'employee-attendance__calendar'}
                    localizer={localizer}
                    events={state.calendarEventsData}
                    components={{
                        dateCellWrapper: (props) => (
                            <AttendanceCalendarDateCell
                                {...props}
                                selectedDate={selectedDateCell?.toDate() ?? null}
                                onDateClick={(date) => {
                                    setSelectedDateCell(dayjs(date));
                                }}
                            />
                        ),
                        event: (calendarProps) => (
                            <AttendanceCalendarEvent
                                {...calendarProps}
                            />),
                        toolbar: (toolbarProps) => (
                            <Toolbar
                                {...toolbarProps}
                                onDateChange={onDateChange}
                            />
                        )
                    }}
                />
                <div className="employee-attendance__punch-panel">
                    <div className="employee-attendance__punch-header">
                        <h6 className="employee-attendance__punch-date">{selectedDateCell?.format(Locale.displayDateLong)}</h6>
                        <span
                            className={`employee-attendance__status-pill ${selectedRecord?.status.toLowerCase()}`}>{removeUnderScoreWithLowerCase(selectedRecord?.status || '')}</span>
                    </div>

                    {selectedRecord ? (
                        <>
                            <div className={'employee-attendance__card-wrapper'}>
                                <div className={'employee-attendance__card'}>
                                    <span className={'employee-attendance__card-title'}>
                                        In Time
                                    </span>
                                    <span className={'employee-attendance__card-value'}>
                                        {selectedRecord.checkInTime}
                                    </span>
                                </div>
                                <div className={'employee-attendance__card'}>
                                    <span className={'employee-attendance__card-title'}>
                                        Out Time
                                    </span>
                                    <span className={'employee-attendance__card-value'}>
                                        {selectedRecord.checkOutTime}
                                    </span>
                                </div>
                                <div className={'employee-attendance__card'}>
                                    <span className={'employee-attendance__card-title'}>
                                        Total Hours
                                    </span>
                                    <span className={'employee-attendance__card-value'}>
                                        {selectedRecord.workingHours}
                                    </span>
                                </div>
                            </div>
                            {selectedRecord.punches && selectedRecord.punches.length > 0 ? (
                                <div className={'employee-attendance__punch-timeline'} key={selectedRecord.id}>
                                    <div className={'employee-attendance__punch-heading'}>Punch Timeline</div>
                                    {selectedRecord.punches.map((punch: any) => (
                                        <div className={'employee-attendance__punch__detail-row'}
                                             key={punch.id}>
                                            <div
                                                className={'employee-attendance__punch__detail-row__value'}>{punch.time}</div>
                                            <Tag
                                                className={`employee-attendance__punch__detail-row__status-pill 
                                    ${replaceSpaceWithHyphen(punch.type).toLowerCase()}`}>
                                                {punch.type}</Tag>
                                        </div>))}
                                </div>
                            ) : (
                                <div className={'employee-attendance__punch-empty-container'}>
                                    <History className={'employee-attendance__punch-empty-icon'}/>
                                    <div className={'employee-attendance__punch-empty-description'}>
                                        No punch activity found
                                    </div>
                                </div>
                            )
                            }
                        </>
                    ) : (
                        <div className={'employee-attendance__punch-empty-container'}>
                            <History className={'employee-attendance__punch-empty-icon'}/>
                            <div className={'employee-attendance__punch-empty-description'}>
                                No Attendance Record Found
                            </div>
                        </div>

                    )}

                </div>
            </LoadingSkeleton>
        </div>
    )
}

export default EmployeeAttendanceCalendar;
